<template>
	<div class="permissions">
		<AddPermissionModal
			v-if="canEdit"
			:is-open="addModalOpen"
			:saving="addModalSaving"
			:submit-error="addModalError"
			@close="closeAddModal"
			@save="handleCreatePermission"
		/>

		<div v-if="loading" class="loading">
			{{ $t("permissionPage.loading") }}
		</div>

		<div v-else-if="error" class="error">
			{{ error }}
		</div>

		<div v-else class="editor">
			<div class="toolbar">
				<div class="actions">
					<label class="search">
						<AppIcon :name="IconName.Search" :size="14" />
						<input v-model="search" type="search" :placeholder="$t('permissionPage.searchPlaceholder')" />
					</label>
					<Button
						v-if="canEdit"
						class="add-permission"
						shape="pill"
						variant="primary"
						size="m"
						:aria-label="$t('permissionPage.addPermission')"
						:title="$t('permissionPage.addPermission')"
						@click="openAddModal"
					>
						<AppIcon :name="IconName.Plus" :size="14" />
						<span class="label">{{ $t("permissionPage.addPermission") }}</span>
					</Button>
				</div>
				<ArkListboxDropdown
					v-if="isMobile"
					class="role-select"
					:model-value="selectedRoleId"
					:options="roleOptions"
					:multiple="false"
					:clearable="false"
					:searchable="false"
					:placeholder="$t('permissionPage.role')"
					@update:model-value="selectRole"
				/>
				<div v-if="hasWriteAccess" class="read-only-preview">
					<ArkBaseSwitch :checked="isReadOnlyPreview" @change="isReadOnlyPreview = $event" />
					<span>{{ $t("permissionPage.readOnlyPreview") }}</span>
				</div>
			</div>

			<div class="matrix-scroll">
				<table class="matrix">
					<colgroup>
						<col class="permission-col" />
						<col v-for="role in displayedRoles" :key="role.id" class="role-col" />
					</colgroup>
					<thead>
						<tr>
							<th class="permission-header" scope="col">{{ $t("permissionPage.permissions") }}</th>
							<th
								v-for="role in displayedRoles"
								:key="role.id"
								scope="col"
								class="role-header"
								:title="role.description ?? undefined"
							>
								<span class="name">{{ formatRoleName(role.name) }}</span>
								<span class="count">{{ role.permissions.length }}</span>
							</th>
						</tr>
					</thead>
					<tbody>
						<template v-for="group in permissionGroups" :key="group.section">
							<tr class="section-row">
								<th class="section-label" scope="rowgroup">{{ $t(`permissionPage.sections.${group.section}`) }}</th>
								<td :colspan="displayedRoles.length"></td>
							</tr>
							<tr v-for="permission in group.permissions" :key="permission.id" class="permission-row">
								<th class="permission-cell" scope="row">
									<span class="title">{{ permissionTitle(permission.name) ?? permission.name }}</span>
									<span v-if="canEdit && permissionTitle(permission.name)" class="technical-name">{{
										permission.name
									}}</span>
									<span v-if="permission.description" class="description">{{ permission.description }}</span>
								</th>
								<td v-for="role in displayedRoles" :key="`${role.id}-${permission.id}`" class="access-cell">
									<input
										v-if="canEdit"
										type="checkbox"
										:checked="isPermissionAssigned(role, permission.id)"
										:disabled="updating"
										:aria-label="`${formatRoleName(role.name)} – ${permission.name}`"
										@change="
											handlePermissionChange(role.id, permission.id, ($event.target as HTMLInputElement).checked)
										"
									/>
									<AppIcon
										v-else-if="isPermissionAssigned(role, permission.id)"
										:name="IconName.Check"
										:size="18"
										class="assigned"
										:aria-label="`${formatRoleName(role.name)} – ${permission.name}`"
									/>
									<span v-else class="unassigned" aria-hidden="true">—</span>
								</td>
							</tr>
						</template>
						<tr v-if="filteredPermissions.length === 0">
							<td class="empty" :colspan="displayedRoles.length + 1">{{ $t("permissionPage.noResults") }}</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue"
import { useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import { useMediaQuery } from "@vueuse/core"
import { Permission, UseRootStore } from "@/model/RootStore"
import AddPermissionModal from "@/components/admin/AddPermissionModal.vue"
import Button from "@/components/ui/Button.vue"
import ArkBaseSwitch from "@/components/ui/ArkBaseSwitch.vue"
import ArkListboxDropdown from "@/components/ui/ArkListboxDropdown.vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import {
	getRolesWithPermissions,
	getAllPermissions,
	assignPermissionToRole,
	createPermission,
	type RoleWithPermissions,
} from "@/utils/ApiClient"
import { formatRoleName, sortRoles } from "@/utils/roles"
import { groupPermissionsBySection } from "@/utils/permissionSections"
import { useToastStore } from "@/stores/toastStore.ts"

const router = useRouter()
const store = UseRootStore()
const toastStore = useToastStore()
const { t, te } = useI18n()

const loading = ref(true)
const error = ref("")
const updating = ref(false)
const roles = ref<RoleWithPermissions[]>([])
const allPermissions = ref<RoleWithPermissions["permissions"]>([])
const search = ref("")
const isMobile = useMediaQuery("(max-width: 768px)")
const selectedRoleId = ref<string | null>(null)

const roleOptions = computed(() =>
	roles.value.map(role => ({ value: role.id, label: `${formatRoleName(role.name)} (${role.permissions.length})` }))
)

const displayedRoles = computed(() => {
	if (!isMobile.value) return roles.value
	const selectedRole = roles.value.find(role => role.id === selectedRoleId.value) ?? roles.value[0]
	return selectedRole ? [selectedRole] : []
})

function selectRole(value: string | number | null | (string | number | null)[]): void {
	if (value != null && !Array.isArray(value)) selectedRoleId.value = String(value)
}

const hasWriteAccess = computed(() => store.currentUser?.has(Permission.AuthorizationWrite) ?? false)
const isReadOnlyPreview = ref(false)
const canEdit = computed(() => hasWriteAccess.value && !isReadOnlyPreview.value)

const filteredPermissions = computed(() => {
	const query = search.value.trim().toLowerCase()
	if (!query) return allPermissions.value
	return allPermissions.value.filter(
		permission =>
			permission.name.toLowerCase().includes(query) ||
			permission.description?.toLowerCase().includes(query) ||
			permissionTitle(permission.name)?.toLowerCase().includes(query)
	)
})

function permissionTitle(permissionName: string): string | null {
	const key = `permissionPage.titles.${permissionName.replace(/\./g, "_")}`
	return te(key) ? t(key) : null
}

const permissionGroups = computed(() => groupPermissionsBySection(filteredPermissions.value))

const addModalOpen = ref(false)
const addModalSaving = ref(false)
const addModalError = ref("")

const openAddModal = () => {
	addModalError.value = ""
	addModalOpen.value = true
}

const closeAddModal = () => {
	addModalOpen.value = false
	addModalError.value = ""
}

const handleCreatePermission = async ({ name, description }: { name: string; description: string }) => {
	try {
		addModalSaving.value = true
		addModalError.value = ""
		const created = await createPermission(store.userApiToken, name, description)
		allPermissions.value = [...allPermissions.value, created].sort((a, b) => a.name.localeCompare(b.name))
		closeAddModal()
		toastStore.success("permissionPage.createSuccess")
	} catch (err) {
		addModalError.value = err instanceof Error ? err.message : "Failed to create permission"
	} finally {
		addModalSaving.value = false
	}
}

const loadData = async () => {
	try {
		loading.value = true
		error.value = ""

		const [rolesData, permissionsData] = await Promise.all([
			getRolesWithPermissions(store.userApiToken),
			getAllPermissions(store.userApiToken),
		])

		roles.value = sortRoles(rolesData)
		if (!selectedRoleId.value) selectedRoleId.value = roles.value[0]?.id ?? null
		allPermissions.value = permissionsData.sort((a, b) => a.name.localeCompare(b.name))
	} catch (err) {
		error.value = err instanceof Error ? err.message : "Failed to load data"
	} finally {
		loading.value = false
	}
}

const isPermissionAssigned = (role: RoleWithPermissions, permissionId: string): boolean => {
	return role.permissions.some(permission => permission.id === permissionId)
}

const handlePermissionChange = async (roleId: string, permissionId: string, checked: boolean) => {
	try {
		updating.value = true

		const role = roles.value.find(r => r.id === roleId)
		if (!role) return

		const currentPermissionIds = role.permissions.map(permission => permission.id)

		// Update the permission list
		let newPermissionIds: string[]
		if (checked) {
			newPermissionIds = [...currentPermissionIds, permissionId]
		} else {
			newPermissionIds = currentPermissionIds.filter((id: string) => id !== permissionId)
		}

		// API now returns the updated role
		const updatedRole = await assignPermissionToRole(
			store.userApiToken,
			Number(roleId),
			newPermissionIds.map(id => Number(id))
		)

		// Simply replace the role in the array
		const roleIndex = roles.value.findIndex(r => r.id === roleId)
		if (roleIndex !== -1) {
			roles.value[roleIndex] = updatedRole
		}
	} catch (err) {
		error.value = err instanceof Error ? err.message : "Failed to update permissions"
	} finally {
		updating.value = false
	}
}

onMounted(async () => {
	if (!store.currentUser?.has(Permission.AuthorizationRead) && !hasWriteAccess.value) {
		router.push("/")
		return
	}

	await loadData()
})
</script>

<style scoped>
.loading,
.error {
	text-align: center;
	padding: var(--spacing-2xl);
	font-size: var(--font-size-medium);
	color: var(--neutral-600);
}

.error {
	color: var(--accent-warning);
}

.toolbar {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	gap: var(--spacing-sm);
	margin-bottom: var(--spacing-md);

	.actions {
		display: flex;
		flex: 1;
		align-items: center;
		flex-wrap: wrap;
		gap: var(--spacing-sm);
	}

	.read-only-preview {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		font-size: var(--font-size-base);
		color: var(--neutral-700);
	}

	.search {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		flex: 0 1 360px;
		min-width: 200px;
		height: var(--size-button-s);
		box-sizing: border-box;
		padding: 0 var(--spacing-md);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-full);
		color: var(--neutral-600);

		&:focus-within {
			border-color: var(--primary-green);
		}

		input {
			flex: 1;
			min-width: 0;
			height: 100%;
			padding: 0;
			border: none;
			outline: none;
			background: none;
			font-family: inherit;
			font-size: var(--font-size-small);
			color: var(--neutral-900);
		}
	}
}

.permissions,
.editor {
	display: flex;
	flex: 1;
	flex-direction: column;
	min-height: 0;
}

.matrix-scroll {
	flex: 1;
	min-height: 0;
	overflow: auto;
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-lg);
	background: var(--neutral-0);
}

.matrix {
	width: 100%;
	border-collapse: separate;
	border-spacing: 0;
	table-layout: fixed;

	.permission-col {
		width: 320px;
	}

	th,
	td {
		border-bottom: var(--border-width-thin) solid var(--neutral-200);
	}

	thead th {
		position: sticky;
		top: 0;
		z-index: 2;
		padding: var(--spacing-smd) var(--spacing-sm);
		background: var(--neutral-50);
		color: var(--neutral-800);
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-semibold);
		vertical-align: bottom;
	}

	.permission-header {
		left: 0;
		z-index: 3;
		text-align: left;
	}

	.role-header {
		text-align: center;

		.name {
			display: block;
			overflow-wrap: anywhere;
		}

		.count {
			display: inline-block;
			margin-top: var(--spacing-xs);
			padding: 0 var(--spacing-sm);
			border-radius: var(--border-radius-full);
			background: var(--neutral-200);
			color: var(--neutral-600);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-medium);
		}
	}

	.permission-cell {
		position: sticky;
		left: 0;
		z-index: 1;
		padding: var(--spacing-sm) var(--spacing-md);
		background: var(--neutral-0);
		border-right: var(--border-width-thin) solid var(--neutral-200);
		text-align: left;
		font-weight: normal;

		.title {
			display: block;
			font-size: var(--font-size-base);
			font-weight: var(--font-weight-medium);
			color: var(--neutral-900);
		}

		.technical-name {
			display: inline-block;
			max-width: 100%;
			margin-top: var(--spacing-xs);
			padding: var(--spacing-xxs) var(--spacing-sm);
			border: var(--border-width-thin) solid color-mix(in srgb, var(--primary-green) 40%, transparent);
			border-radius: var(--border-radius-full);
			background: color-mix(in srgb, var(--primary-green) 10%, transparent);
			font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
			font-size: var(--font-size-xs);
			color: var(--primary-green);
			overflow-wrap: anywhere;
		}

		.description {
			display: block;
			margin-top: var(--spacing-xs);
			font-size: var(--font-size-small);
			color: var(--neutral-600);
		}
	}

	.permission-header {
		border-right: var(--border-width-thin) solid var(--neutral-200);
	}

	.access-cell {
		text-align: center;
		vertical-align: middle;

		input {
			width: 16px;
			height: 16px;
			cursor: pointer;
			accent-color: var(--primary-green);

			&:disabled {
				cursor: not-allowed;
				opacity: 0.6;
			}
		}

		.assigned {
			color: var(--primary-green);
			vertical-align: middle;
		}

		.unassigned {
			color: var(--neutral-400);
		}
	}

	.section-row {
		th,
		td {
			background: var(--neutral-100);
		}

		.section-label {
			position: sticky;
			left: 0;
			z-index: 1;
			padding: var(--spacing-sm) var(--spacing-md);
			color: var(--primary-green);
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-bold);
			text-align: left;
			text-transform: uppercase;
		}
	}

	.permission-row:hover {
		th,
		td {
			background: var(--neutral-50);
		}
	}

	tbody tr:last-child {
		th,
		td {
			border-bottom: none;
		}
	}

	.empty {
		padding: var(--spacing-xl);
		text-align: center;
		color: var(--neutral-600);
	}
}

@media (max-width: 768px) {
	.toolbar {
		.actions {
			flex: 1 1 100%;
			flex-wrap: nowrap;
		}

		.search {
			flex: 1;
			min-width: 0;
		}

		.add-permission {
			flex-shrink: 0;
			width: var(--size-button-s);
			padding: 0;

			.label {
				display: none;
			}
		}

		.role-select {
			flex: 1 1 100%;
		}
	}

	.matrix {
		thead {
			display: none;
		}

		.permission-col {
			width: auto;
		}

		.role-col {
			width: 56px;
		}

		.section-row .section-label {
			padding: var(--spacing-xs) var(--spacing-smd);
		}

		.permission-cell {
			padding: var(--spacing-sm) var(--spacing-smd);

			.technical-name {
				padding: 0 var(--spacing-xs);
			}
		}
	}
}
</style>
